import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { Port } from "../../pages/PortsAdmin";
import { captureError } from "../../lib/posthog";

interface DestinationPortsTabProps {
  ports: Port[];
  loading: boolean;
  refreshPorts: () => Promise<void>;
}

const DestinationPortsTab: React.FC<DestinationPortsTabProps> = ({
  ports,
  loading,
  refreshPorts,
}) => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPort, setEditingPort] = useState<Port | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showAddModal = () => {
    setEditingPort(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (port: Port) => {
    setEditingPort(port);
    form.setFieldsValue({
      name: port.name,
      country: port.country,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      if (editingPort) {
        // Update existing port
        const { error } = await supabase
          .from("booking_destination_port")
          .update({
            name: values.name,
            country: values.country,
          })
          .eq("id", editingPort.id);

        if (error) throw error;
        message.success("Destination port updated successfully");
      } else {
        // Create new port
        const { error } = await supabase
          .from("booking_destination_port")
          .insert([
            {
              name: values.name,
              country: values.country,
            },
          ]);

        if (error) throw error;
        message.success("Destination port added successfully");
      }

      setIsModalVisible(false);
      refreshPorts();
    } catch (error) {
      console.error("Error saving port:", error);
      message.error("Failed to save port");
      captureError(error, {
        source: "DestinationPortsTab",
        action: "savePort",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const { error } = await supabase
        .from("booking_destination_port")
        .delete()
        .eq("id", id);

      if (error) throw error;
      message.success("Destination port deleted successfully");
      refreshPorts();
    } catch (error) {
      console.error("Error deleting port:", error);
      message.error("Failed to delete port");
      captureError(error, {
        source: "DestinationPortsTab",
        action: "deletePort",
      });
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: Port, b: Port) => a.name.localeCompare(b.name),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: (a: Port, b: Port) =>
        (a.country || "").localeCompare(b.country || ""),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: Port) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            size="small"
          />
          <Popconfirm
            title="Are you sure you want to delete this port?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
          Add Destination Port
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={ports.map((port) => ({ ...port, key: port.id }))}
        loading={loading}
        pagination={{ pageSize: 10 }}
        rowKey="id"
      />

      <Modal
        title={editingPort ? "Edit Destination Port" : "Add Destination Port"}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        confirmLoading={isSubmitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Port Name"
            rules={[{ required: true, message: "Please enter port name" }]}
          >
            <Input placeholder="Enter port name" />
          </Form.Item>
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: "Please enter country" }]}
          >
            <Input placeholder="Enter country" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DestinationPortsTab;
